/**
 * Module: web/src/lib/format.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.4.1
 * Date: 2026-07-11
 *
 * Tiny shared display formatters.
 *
 * v1.1.0 (P9 Review Incentives): fmtDateChicago -- review/paid dates render
 * in America/Chicago per the review-incentives contract (the legacy tool's
 * biggest date gotcha was rendering UTC verbatim, recon doc 60 sect 10.2);
 * fmtMoney -- shared optional-USD renderer (decision #133 amounts).
 * v1.2.0 (P10 Call History): fmtDateTimeChicago -- call timestamps render
 * company-local (contract call-history-api s6 "Date/Time CT" column);
 * fmtDurationHMS -- seconds -> "1h 14m 38s" / "4m 05s" / "38s" for call
 * durations + the stats cards.
 * v1.3.0 (P11 call attribution FE): fmtTimeChicago -- second-precision CT
 * wall time for the journey timeline rows (contract s6 v1.3.0);
 * fmtPhoneDisplay -- NANP numbers render "+1 512 241 9104" (CNAM header
 * pairing), non-NANP strings pass through verbatim.
 * v1.4.0 (P12 System Config): fmtRelativeTime -- "3 minutes ago" /
 * "2 days ago" for secret rotated_at + GoTo token mtime rows (contract
 * system-config-api sect 7 "rotated <rel-time>" masked-row format).
 * v1.4.1: fmtRelativeTime clamps FUTURE deltas to "just now" -- every
 * caller renders past-event recency (rotated_at / mtimes / verify-at), and
 * ordinary VPS-vs-client clock skew (~1 min observed in P12 E2E) made a
 * just-rotated secret read "rotated in 1 minute". A server timestamp ahead
 * of the local clock is skew, not the future.
 */

/** ISO timestamp -> locale short date+time; null/invalid -> em-dash. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * ISO timestamp -> America/Chicago calendar date (company-local display,
 * review-incentives contract sect 3/sect 7). The legacy paid_at noon-UTC
 * convention lands on the SAME calendar date in Chicago by design.
 */
export function fmtDateChicago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: 'short', day: 'numeric',
  });
}

/**
 * ISO timestamp -> America/Chicago short date + time (company-local display,
 * call-history contract s6: the list column and detail header are "CT").
 */
export function fmtDateTimeChicago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * ISO timestamp -> America/Chicago wall time with seconds ("7:13:11 AM") --
 * the journey-timeline row clock (call-history contract s6 v1.3.0: events
 * inside one call are same-day, so time-only keeps the rows compact).
 */
export function fmtTimeChicago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

/**
 * Phone number -> spaced NANP display ("+1 512 241 9104"). Ten-digit inputs
 * render without the +1; anything non-NANP (short codes, international,
 * already-formatted oddities) passes through verbatim; null -> em-dash.
 */
export function fmtPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return phone;
}

/** Whole seconds -> compact "1h 14m 38s" / "4m 05s" / "38s"; null -> em-dash. */
export function fmtDurationHMS(secs: number | null | undefined): string {
  if (secs === null || secs === undefined || !Number.isFinite(secs) || secs < 0) return '—';
  const total = Math.round(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Optional USD amount (number | numeric-string | null) -> "$100.00" | em-dash. */
export function fmtMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Arbitrary JSONB detail -> short single-line preview string. */
export function fmtDetail(detail: unknown): string {
  if (detail === null || detail === undefined || detail === '') return '—';
  if (typeof detail === 'string') return detail;
  try {
    const s = JSON.stringify(detail);
    return s.length > 120 ? `${s.slice(0, 117)}…` : s;
  } catch {
    return String(detail);
  }
}

/**
 * ISO timestamp -> coarse PAST-recency phrase ("just now", "5 minutes ago",
 * "3 days ago"). Every caller renders a past event (rotation, file mtime,
 * verify run) -- a timestamp ahead of the local clock is server/client
 * skew, so future deltas clamp to "just now" (v1.4.1). Coarse on purpose:
 * recency is a freshness cue, not a clock.
 */
export function fmtRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const deltaS = Math.round((Date.now() - d.getTime()) / 1000); // positive = past
  if (deltaS < 45) return 'just now'; // includes clock-skew "future" values
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'always' });
  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'minute'],
    [3600, 'hour'],
    [86400, 'day'],
    [86400 * 30, 'month'],
    [86400 * 365, 'year'],
  ];
  let unit: Intl.RelativeTimeFormatUnit = 'minute';
  let div = 60;
  for (const [secs, u] of steps) {
    if (deltaS >= secs) {
      unit = u;
      div = secs;
    }
  }
  return rtf.format(-Math.trunc(deltaS / div), unit);
}
